import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ArrowUpRight, Inbox, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, inr } from "@/lib/api";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

const Panel = ({ children, className = "", i = 0, ...rest }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    className={`rounded-3xl border border-white/10 bg-[#0E1526] p-6 sm:p-8 ${className}`} {...rest}>{children}</motion.div>
);

const Purchases = ({ items }) => (
  <Panel i={2} className="lg:col-span-12" data-testid="dashboard-purchases">
    <div className="flex items-center justify-between">
      <h2 className="font-display text-2xl font-bold text-white">Your purchases</h2>
      <span className="font-mono text-xs text-slate-400">{items.length} paid</span>
    </div>
    {items.length === 0 ? (
      <p className="mt-6 text-sm text-slate-400" data-testid="dashboard-purchases-empty">No payments yet.</p>
    ) : (
      <ul className="mt-6 divide-y divide-white/10" data-testid="dashboard-purchases-list">
        {items.map((p) => (
          <li key={p.order_id} className="grid gap-2 py-5 sm:grid-cols-[8rem_1fr_auto] sm:gap-6" data-testid={`dashboard-purchase-${p.order_id}`}>
            <span className="font-mono text-xs text-slate-400">{fmt(p.paid_at || p.created_at)}</span>
            <div>
              <p className="text-white">{p.plan} · <span className="font-mono">{inr(p.amount)}</span></p>
              <p className="mt-1 text-sm text-slate-400">{p.services?.join(", ")}</p>
            </div>
            <span className="h-fit w-fit rounded-full bg-[#00F0FF]/15 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-[#00F0FF]">paid</span>
          </li>
        ))}
      </ul>
    )}
  </Panel>
);

const Requests = ({ items }) => (
  <Panel i={2} className="lg:col-span-12" data-testid="dashboard-requests">
    <div className="flex items-center justify-between">
      <h2 className="font-display text-2xl font-bold text-white">Your service requests</h2>
      <span className="font-mono text-xs text-slate-400">{items.length} total</span>
    </div>
    {items.length === 0 ? (
      <div className="mt-8 flex flex-col items-start gap-4 rounded-2xl border border-dashed border-white/15 p-8" data-testid="dashboard-requests-empty">
        <Inbox className="h-6 w-6 text-[#00F0FF]" />
        <p className="text-slate-300">No requests yet. Pick services and send an enquiry — it will show up here.</p>
        <Link to="/#services" data-testid="dashboard-browse-services" className="rounded-full bg-[#00F0FF] px-5 py-2.5 text-sm font-semibold text-[#070B14]">Browse services</Link>
      </div>
    ) : (
      <ul className="mt-6 divide-y divide-white/10" data-testid="dashboard-requests-list">
        {items.map((r) => (
          <li key={r.contact_id} className="grid gap-2 py-5 sm:grid-cols-[8rem_1fr_auto] sm:gap-6" data-testid={`dashboard-request-${r.contact_id}`}>
            <span className="font-mono text-xs text-slate-400">{fmt(r.created_at)}</span>
            <div>
              <p className="text-white">{r.services?.length ? r.services.join(", ") : "General enquiry"}</p>
              <p className="mt-1 line-clamp-2 text-sm text-slate-400">{r.message}</p>
              {(r.budget || r.call_time) && <p className="mt-1 font-mono text-xs text-slate-400">{[r.budget && `Budget: ${r.budget}`, r.call_time && `Call: ${r.call_time}`].filter(Boolean).join(" · ")}</p>}
            </div>
            <span className="h-fit w-fit rounded-full border border-[#00F0FF]/40 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-[#00F0FF]">{r.status}</span>
          </li>
        ))}
      </ul>
    )}
  </Panel>
);

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const me = user || location.state?.user;

  useEffect(() => {
    if (!loading && !me) navigate("/", { replace: true });
  }, [loading, me, navigate]);

  useEffect(() => {
    if (!me) return;
    api.get("/me/requests").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
    api.get("/me/payments").then((r) => setPayments(Array.isArray(r.data) ? r.data : [])).catch(() => setPayments([]));
  }, [me]);

  if (!me) return <div className="min-h-screen bg-[#070B14]" data-testid="dashboard-loading" />;

  const out = async () => { await logout(); navigate("/"); };

  return (
    <main className="min-h-screen px-5 pt-32 pb-20 sm:px-8 lg:px-12" data-testid="dashboard-page">
      <div className="mx-auto max-w-6xl">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF]">Dashboard</motion.p>
        <div className="overflow-hidden">
          <motion.h1 initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 font-display text-4xl font-extrabold tracking-tight text-white sm:text-6xl" data-testid="dashboard-greeting">
            Hello, {me.name?.split(" ")[0] || "there"}.
          </motion.h1>
        </div>
        <div className="mt-12 grid gap-5 lg:grid-cols-12">
          <Panel i={0} className="lg:col-span-5" data-testid="dashboard-account-card">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Account</p>
            <div className="mt-6 flex items-center gap-4">
              {me.picture ? <img src={me.picture} alt="" className="h-16 w-16 rounded-2xl object-cover" /> : <span className="grid h-16 w-16 place-items-center rounded-2xl bg-[#00F0FF]/15 font-display text-2xl text-[#00F0FF]">{me.name?.[0]}</span>}
              <div className="min-w-0">
                <p className="truncate font-display text-xl font-bold text-white" data-testid="dashboard-account-name">{me.name}</p>
                <p className="truncate text-sm text-slate-300" data-testid="dashboard-account-email">{me.email}</p>
              </div>
            </div>
            <dl className="mt-8 grid grid-cols-2 gap-4 border-t border-white/10 pt-6 text-sm">
              <div><dt className="text-slate-400">Member since</dt><dd className="mt-1 text-white" data-testid="dashboard-member-since">{fmt(me.created_at)}</dd></div>
              <div><dt className="text-slate-400">Sign-in</dt><dd className="mt-1 text-white">Google</dd></div>
            </dl>
            <button onClick={out} data-testid="dashboard-logout-button" className="mt-8 flex items-center gap-2 text-sm text-slate-300 transition-colors hover:text-white"><LogOut className="h-4 w-4" />Sign out</button>
          </Panel>
          <Panel i={1} className="relative overflow-hidden lg:col-span-7" data-testid="dashboard-plan-card">
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#00F0FF]/10 blur-3xl" />
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Current plan</p>
            <p className="mt-4 flex items-center gap-3 font-display text-4xl font-bold text-white" data-testid="dashboard-plan-name"><Sparkles className="h-7 w-7 text-[#00F0FF]" />{me.plan || "Explorer"}</p>
            <p className="mt-3 max-w-md text-sm text-slate-300">{payments.length ? `${payments.length} paid order${payments.length > 1 ? "s" : ""} — we'll be in touch to get everything set up.` : "No active package yet. Pay for a service, build a custom plan or take the complete suite."}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/" state={{ to: "packages" }} data-testid="dashboard-upgrade-button" className="group flex items-center gap-2 rounded-full bg-[#00F0FF] px-5 py-3 text-sm font-semibold text-[#070B14]">
                View packages from {inr(6999)} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
              </Link>
              <Link to="/" state={{ to: "contact" }} data-testid="dashboard-contact-button" className="rounded-full border border-white/20 px-5 py-3 text-sm text-white hover:border-[#00F0FF]">Talk to us</Link>
            </div>
          </Panel>
          <Purchases items={payments} />
          <Requests items={items} />
        </div>
      </div>
    </main>
  );
}
